import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { FeaturedTransporters } from "../components/FeaturedTransporters";
import { Button } from "../components/ui/button";
import { IMAGES } from "../lib/constants";
import { ArrowRight, Truck, ShieldCheck, Search, Store, PackageCheck, Boxes, Gauge, FileCheck } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="border-b border-foreground">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-4 sm:px-8 py-14 lg:py-24 flex flex-col justify-center border-r-0 lg:border-r border-foreground">
            <span className="label-caps text-primary mb-6" data-testid="hero-eyebrow">Controlled B2B Marketplace · Tyres · Stock · Freight · Export</span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
              Source stock.<br />Post transport jobs.<br /><span className="text-primary">Move goods with verified partners.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-xl">
              WILTOM TradeLink connects approved suppliers, buyers and verified transporters.
              List tyres, truck parts, commercial stock and export goods — or post a transport job and
              receive quotes from vetted Sprinter, van and truck drivers across Europe.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8 max-w-xl">
              <Link to="/marketplace" data-testid="hero-browse-btn">
                <Button className="w-full rounded-none bg-primary hover:bg-primary/90 h-12 px-6 text-base">
                  <Search className="mr-2 h-4 w-4" /> Browse Marketplace
                </Button>
              </Link>
              <Link to="/transport" data-testid="hero-post-job-btn">
                <Button className="w-full rounded-none bg-foreground hover:bg-foreground/90 text-white h-12 px-6 text-base">
                  <Truck className="mr-2 h-4 w-4" /> Post Transport Job
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-transporter-btn">
                <Button variant="outline" className="w-full rounded-none h-auto min-h-12 py-2 px-4 text-sm sm:text-base border-foreground hover:bg-foreground hover:text-white whitespace-normal leading-tight text-center">
                  Register as Transporter / Sprinter Driver
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-supplier-btn">
                <Button variant="outline" className="w-full rounded-none h-12 px-6 text-base border-foreground hover:bg-foreground hover:text-white">
                  Register as Supplier
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 min-h-[300px] lg:min-h-full">
            <img src={IMAGES.hero} alt="European logistics truck" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-white/20">
          {[["Approved", "Suppliers"], ["Verified", "Transporters"], ["Sprinter → 40t", "Any load size"], ["Admin", "Controlled"]].map(([a, b], i) => (
            <div key={i} className="px-6 py-8">
              <div className="font-display font-black text-2xl">{a}</div>
              <div className="label-caps text-white/60 mt-1">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Two pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-24">
        <span className="label-caps text-primary">Two platforms, one account</span>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-3 mb-12 max-w-3xl">
          A marketplace for business stock and a transport exchange for any delivery — all managed through admin approval.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 border border-foreground">
          <div className="p-8 border-b md:border-b-0 md:border-r border-foreground" data-testid="pillar-marketplace">
            <Boxes className="h-8 w-8 text-primary mb-4" />
            <h3 className="font-display font-bold text-2xl tracking-tight mb-2">1 · Marketplace</h3>
            <p className="text-sm text-muted-foreground mb-4">List and source new & used tyres, truck parts, commercial stock and export goods with full specs and photos.</p>
            <img src={IMAGES.tyres} alt="Tyre warehouse" className="w-full h-44 object-cover border border-border" />
            <Link to="/marketplace"><Button variant="ghost" className="rounded-none mt-4 px-0 hover:bg-transparent text-primary">Browse listings <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
          <div className="p-8" data-testid="pillar-transport">
            <Truck className="h-8 w-8 text-primary mb-4" />
            <h3 className="font-display font-bold text-2xl tracking-tight mb-2">2 · Transport Jobs</h3>
            <p className="text-sm text-muted-foreground mb-4">From a Sprinter or van delivery to a 40t container — any approved company can post a job (box pickup, furniture, pallets, tyre pickup, export goods) and pick the best quote from verified drivers.</p>
            <img src={IMAGES.fleet} alt="Truck fleet" className="w-full h-44 object-cover border border-border" />
            <Link to="/transport"><Button variant="ghost" className="rounded-none mt-4 px-0 hover:bg-transparent text-primary">Post a transport job <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-foreground bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
          <span className="label-caps text-primary">How it works</span>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border mt-6">
            {[
              { icon: FileCheck, title: "Register & verify", text: "Suppliers and transporters sign up. Admin reviews documents and approves real businesses only." },
              { icon: Store, title: "List or post", text: "Approved suppliers list stock; approved companies post transport jobs with full load details." },
              { icon: PackageCheck, title: "Inquire & quote", text: "Buyers send inquiries; transporters apply with a quote. Contact on-platform or via WhatsApp." },
              { icon: ShieldCheck, title: "Move with trust", text: "Companies choose the best transporter. Admin oversees disputes and blocks bad actors." },
            ].map((s, i) => (
              <div key={i} className="bg-white p-7" data-testid={`how-step-${i}`}>
                <div className="h-11 w-11 bg-primary/5 border border-primary/20 flex items-center justify-center mb-5">
                  <s.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles split */}
      <section className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2">
        <div className="px-4 sm:px-8 py-16 border-b lg:border-b-0 lg:border-r border-foreground">
          <Gauge className="h-8 w-8 text-primary mb-4" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-4">For suppliers & companies</h2>
          <ul className="space-y-3">
            {["List tyres, parts, commercial stock and export goods", "Reach verified B2B buyers across Europe", "Post transport jobs and receive competitive quotes", "Manage inquiries and applicants from one dashboard"].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm"><PackageCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
          <img src={IMAGES.containers} alt="Export containers" className="mt-8 w-full h-56 object-cover border border-border" />
        </div>
        <div className="px-4 sm:px-8 py-16">
          <Truck className="h-8 w-8 text-primary mb-4" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-4">For transporters & drivers</h2>
          <ul className="space-y-3">
            {["Sprinter, van, 3.5t up to 40t & container trucks", "Upload insurance & vehicle documents for verification", "Browse open jobs and apply with your quote", "Win work that matches your routes and service area"].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm"><Truck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
          <img src={IMAGES.warehouse} alt="Warehouse pallets" className="mt-8 w-full h-56 object-cover border border-border" />
        </div>
      </section>

      <FeaturedTransporters limit={6} />

      {/* CTA */}
      <section className="border-t border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">Ready to trade and move goods?</h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Join a controlled B2B network of verified suppliers and transporters.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/register" data-testid="cta-supplier-btn"><Button className="rounded-none bg-primary hover:bg-primary/90 h-12 px-8 text-base">Register as Supplier</Button></Link>
            <Link to="/register" data-testid="cta-transporter-btn"><Button variant="outline" className="rounded-none h-auto min-h-12 py-2 px-6 text-sm sm:text-base border-white text-white hover:bg-white hover:text-foreground whitespace-normal leading-tight text-center">Register as Transporter / Sprinter Driver</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-foreground py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-sm text-muted-foreground">
          <span className="font-display font-bold text-foreground">WILTOM TradeLink</span>
          <nav className="flex flex-wrap items-center gap-4">
            <Link to="/find-transporter" className="hover:text-foreground" data-testid="landing-find-transporter">Find Transporter</Link>
            <Link to="/impressum" className="hover:text-foreground" data-testid="landing-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-foreground" data-testid="landing-datenschutz">Datenschutz</Link>
            <Link to="/terms" className="hover:text-foreground" data-testid="landing-terms">Terms</Link>
          </nav>
          <span>© {new Date().getFullYear()} WILTOM TradeLink</span>
        </div>
      </footer>
    </div>
  );
}
